import React from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui icons
import Check from '@material-ui/icons/Check';
import User from '@material-ui/core/SvgIcon/SvgIcon';
// @material-ui/core components
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { userRanks, paidAdCurrency } from 'config';
// our components
import adCreateStyles from 'assets/jss/AdsCreate';
import { PAID_AD } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import InfoUser from 'components/common/InfoUser';
import Loading from 'components/common/Loading';
import UserSelect from 'components/User/Select';
import Datepicker from 'components/common/DatePicker';
import AdsSelect from 'components/PaidAd/SelectAds';
import WithPermission from 'components/User/WithPermission';
import AuthContext from 'helpers/authContext';
import { allowPaidAdChangeCurrency } from 'helpers/functions';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */

const translateValidationSchema = t =>
  Yup.object({
    ad: Yup.string().required(t('errors.required')),
    fromOwner: Yup.boolean(),
    owner: Yup.object()
      .nullable()
      .when('fromOwner', {
        is: false,
        then: Yup.object()
          .nullable()
          .required(t('errors.required')),
      }),
    pack: Yup.object(),
    promotion: Yup.bool(),
    free: Yup.bool(),
    promotionAds: Yup.array().when('promotion', {
      is: true,
      then: Yup.array()
        .min(4, t('paidAd.form.error_limit_promo'))
        .max(4, t('paidAd.form.error_limit_promo')),
    }),
  });

/**
 * Initial values to AdForm
 */
const initialValues = {
  ad: '',
  // eslint-disable-next-line no-underscore-dangle
  date: moment(),
  promotion: false,
  promotionAds: [],
  agency: null,
  owner: null,
  pack: null,
  free: false,
  fromOwner: false,
  currency: 'USD',
  secondaryCurrency: null,
};

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

class PaidAdForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    this.state = {
      fields: {
        ...initialValues,
      },
      showSelectAgency: false,
      showSelectOwner: false,
      pickedSetup: null,
      exchangeRate: null,
    };
    this.formikRef = null;
    moment.locale(props.i18n.language);
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(PAID_AD.LIST);
  };

  /**
   * @description
   * Updating before send to parent.
   *
   * @param {*} values - New values to update fields.
   * @returns {*} Undefined - No returns.
   */
  handleSubmit = values => {
    const { onSave } = this.props;
    const { pickedSetup } = this.state;
    const { __typename: typename, ...frequency } = values.pack.frequency;
    const { __typename: typename1, ...duration } = pickedSetup.duration;

    const promotionAdsID = values.promotionAds.map(promoAds => promoAds.id);
    const finalValues = {
      ad: values.ad,
      frequency,
      schedule: pickedSetup.schedule,
      duration,
      owner: values.owner ? values.owner.id : undefined,
      free: values.free ? values.free : undefined,
      startDate: values.date.format() || null,
      promotion:
        values.promotion && pickedSetup.promotion
          ? pickedSetup.promotion.id
          : undefined,
      promotionArgs: values.promotion ? `[${promotionAdsID}]` : undefined,
      secondaryCurrency: values.secondaryCurrency,
    };
    if (values.fromOwner) return onSave(finalValues, true);
    return onSave(finalValues, false);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   *
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Show / Hide Agency (manager) field
   *
   * @param {Object} manager - Data of manager user.
   * @param {Function} handleChange - Passed from Formik.
   * @returns {*} - React node.
   */
  renderAgencyField = (manager, handleChange) => {
    const { classes, t } = this.props;
    const { showSelectAgency } = this.state;
    return (
      <div>
        <SweetAlert
          title=""
          showConfirm={false}
          showCancel={false}
          onConfirm={() => {}}
          customClass={classes.adModalConfirmation}
          show={showSelectAgency}
          closeOnClickOutside
          onCancel={() =>
            this.setState({
              showSelectAgency: !showSelectAgency,
            })
          }
        >
          <UserSelect
            listAgencies
            selectUser={agency => {
              handleChange({
                target: {
                  name: 'agency',
                  value: agency,
                },
              });
              // reset user owner
              if (!manager || (manager && agency.id !== manager.id)) {
                handleChange({
                  target: {
                    name: 'owner',
                    value: null,
                  },
                });
              }

              this.setState({
                showSelectAgency: !showSelectAgency,
              });
            }}
          />
        </SweetAlert>
        <div className={classes.section}>
          <h6>{t('paidAd.form.fields.agency')}</h6>
          <div className={classes.categorySection}>
            {manager && <InfoUser user={manager} />}
            <React.Fragment>
              <Button
                simple
                color="primary"
                onClick={() =>
                  this.setState({
                    showSelectAgency: true,
                  })
                }
              >
                {manager
                  ? t('ad.form.fields.change')
                  : t('ad.form.fields.select')}
              </Button>
              {manager && (
                <Button
                  simple
                  color="primary"
                  onClick={() => {
                    handleChange({
                      target: {
                        name: 'agency',
                        value: null,
                      },
                    });
                    handleChange({
                      target: {
                        name: 'owner',
                        value: null,
                      },
                    });
                    handleChange({
                      target: {
                        name: 'promotion',
                        value: false,
                      },
                    });
                    handleChange({
                      target: {
                        name: 'promotionAds',
                        value: [],
                      },
                    });
                  }}
                >
                  {t('paidAd.form.clean')}
                </Button>
              )}
            </React.Fragment>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      classes,
      errors: serverErrors,
      submitLoading,
      t,
      packs,
      exchangeRates,
      closeNotification,
    } = this.props;
    const { showSelectOwner, pickedSetup, fields, exchangeRate } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);

    const AdRow = ({ data }) => (
      <div style={{ fontSize: 12, whiteSpace: 'normal' }}>
        <div className={classes.adItem}>
          <span style={{ marginRight: 10 }}>ID Auto {data.id}</span>
          {data.owner && (
            <span style={{ color: '#227db2' }}>
              <User /> {data.owner.fullname}
            </span>
          )}
        </div>
        <div className={classes.adItem}>
          <span>ID Anuncio {data.ad.id}</span>
        </div>
        <div className={classes.adItem}>
          <span>{data.ad.title}</span>
        </div>
      </div>
    );
    return (
      <AuthContext.Consumer>
        {/* eslint-disable-next-line consistent-return */}
        {user => {
          const hasGeneral =
            user.allPermissions.filter(
              permission => permission.codename === 'add_autopaidad',
            ).length === 1;
          return (
            <React.Fragment>
              <Snackbars
                place="tc"
                message={all || ''}
                open={!!all}
                color="danger"
                closeNotification={closeNotification}
                close
              />

              <Formik
                ref={ref => {
                  this.formikRef = ref;
                }}
                initialValues={fields}
                validationSchema={validationSchema}
                onSubmit={values => this.handleSubmit(values)}
                render={({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  handleBlur,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.pageContainer}>
                      <div
                        className={`${classes.row} ${classes.contentBtns}`}
                        style={{ justifyContent: 'space-between' }}
                      >
                        <h3>{t('paidAd.form.create')}</h3>
                      </div>

                      <Card>
                        <CardBody>
                          <Loading show={submitLoading} />
                          <GridContainer>
                            <GridItem xs={12} sm={6}>
                              <div style={{ marginBottom: 20 }}>
                                <TextField
                                  label={t('paidAd.form.fields.id')}
                                  name="ad"
                                  error={touched.ad && !!errors.ad}
                                  helperText={touched.ad ? errors.ad : ''}
                                  InputProps={{
                                    value: values.ad,
                                    onChange: e => {
                                      handleChange(e);
                                    },
                                    onBlur: handleBlur,
                                    type: 'number',
                                  }}
                                />
                              </div>
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              <Datepicker
                                title={t('paidAd.form.fields.date')}
                                defaultValue=""
                                required
                                fromToday
                                dateTime
                                onChange={date => {
                                  handleChange({
                                    target: {
                                      name: 'date',
                                      value: date || '',
                                    },
                                  });
                                }}
                                value={values.date}
                              />
                            </GridItem>
                          </GridContainer>
                          {/* frequency */}
                          <div className={classes.section}>
                            <h6
                              style={{
                                color:
                                  touched.pack && errors.pack ? 'red' : 'black',
                              }}
                            >
                              {t('paidAd.form.fields.frequency')}
                            </h6>

                            {packs.map((pack, key) => (
                              <Button
                                key={`frequency-${pack.configuration[0].price}`}
                                simple={
                                  values.pack
                                    ? ReadableDate(pack.frequency) !==
                                      ReadableDate(values.pack.frequency)
                                    : true
                                }
                                round
                                id={`frequency-${key}`}
                                color="info"
                                onClick={() => {
                                  handleChange({
                                    target: {
                                      name: 'pack',
                                      value: pack,
                                    },
                                  });
                                  handleChange({
                                    target: {
                                      name: 'promotion',
                                      value: false,
                                    },
                                  });
                                  handleChange({
                                    target: {
                                      name: 'promotionAds',
                                      value: [],
                                    },
                                  });
                                  this.setState({
                                    pickedSetup: pack.configuration[0],
                                  });
                                }}
                              >
                                {ReadableDate(pack.frequency)}
                              </Button>
                            ))}

                            {pickedSetup && (
                              <p>
                                {t(
                                  // eslint-disable-next-line max-len
                                  `paidAd.form.fields.schedule.${pickedSetup.schedule}`,
                                )}
                              </p>
                            )}
                            {touched.pack && errors.pack && (
                              <p className={classes.errorCategoryField}>
                                {t('errors.required')}
                              </p>
                            )}
                          </div>
                          {values.pack &&
                          values.pack.configuration.length > 1 ? (
                            <div className={classes.section}>
                              <h6>{t('paidAd.form.fields.duration')}</h6>

                              {values.pack.configuration.map(setup => (
                                <Button
                                  key={`duration-${ReadableDate(
                                    setup.duration,
                                  )}`}
                                  simple={
                                    ReadableDate(pickedSetup.duration) !==
                                    ReadableDate(setup.duration)
                                  }
                                  round
                                  color="info"
                                  onClick={() => {
                                    this.setState({
                                      pickedSetup: setup,
                                    });
                                  }}
                                >
                                  {ReadableDate(setup.duration)}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            values.pack && (
                              <div className={classes.section}>
                                <h6>{t('paidAd.form.fields.duration')}</h6>
                                <p>
                                  {ReadableDate(
                                    values.pack.configuration[0].duration,
                                  )}
                                </p>
                              </div>
                            )
                          )}
                          {pickedSetup && (
                            <div className={classes.section}>
                              <div>
                                <WithPermission
                                  allowPermissionFunc={
                                    allowPaidAdChangeCurrency
                                  }
                                >
                                  <div className={classes.section}>
                                    <h6
                                      style={{
                                        color:
                                          touched.pack && errors.pack
                                            ? 'red'
                                            : 'black',
                                      }}
                                    >
                                      {t('paidAd.form.fields.currency')}
                                    </h6>
                                    {Object.keys(paidAdCurrency).map(
                                      (currency, key) => {
                                        const rate = exchangeRates.find(
                                          value =>
                                            value.currencyFrom ===
                                              fields.currency &&
                                            value.currencyTo === currency,
                                        );
                                        if (
                                          fields.currency !== currency &&
                                          !rate
                                        )
                                          return null;

                                        return (
                                          <Button
                                            key={`currency-${currency}`}
                                            simple={
                                              fields.secondaryCurrency
                                                ? fields.secondaryCurrency !==
                                                  currency
                                                : fields.currency !== currency
                                            }
                                            round
                                            id={`currency-${key}`}
                                            color="info"
                                            onClick={() => {
                                              handleChange({
                                                target: {
                                                  name: 'secondaryCurrency',
                                                  value:
                                                    fields.currency !==
                                                      currency && currency,
                                                },
                                              });
                                              this.setState({
                                                fields: {
                                                  ...fields,
                                                  secondaryCurrency:
                                                    fields.currency !==
                                                      currency && currency,
                                                },
                                                exchangeRate:
                                                  fields.currency !==
                                                    currency && rate.rate,
                                              });
                                            }}
                                          >
                                            {currency}
                                          </Button>
                                        );
                                      },
                                    )}
                                    {touched.pack && errors.pack && (
                                      <p className={classes.errorCategoryField}>
                                        {t('errors.required')}
                                      </p>
                                    )}
                                  </div>
                                </WithPermission>
                              </div>
                              <div>
                                <h6>{t('paidAd.form.fields.price')}</h6>
                                {values.free ||
                                values.promotionAds.length === 4 ? (
                                  <p>
                                    {`0 ${
                                      values.secondaryCurrency
                                        ? values.secondaryCurrency
                                        : values.currency
                                    }`}
                                  </p>
                                ) : (
                                  <p>
                                    {values.secondaryCurrency
                                      ? `${
                                          exchangeRate
                                            ? pickedSetup.price * exchangeRate
                                            : ''
                                        } ${values.secondaryCurrency}`
                                      : // eslint-disable-next-line max-len
                                        `${pickedSetup.price} ${values.currency}`}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {!values.fromOwner &&
                            (hasGeneral || user.rank === userRanks.ADMIN) &&
                            this.renderAgencyField(values.agency, handleChange)}
                          {/* show owner selection modal  */}
                          {!values.fromOwner && (
                            <React.Fragment>
                              <SweetAlert
                                title=""
                                showConfirm={false}
                                showCancel={false}
                                onConfirm={() => {}}
                                customClass={classes.adModalConfirmation}
                                show={showSelectOwner}
                                closeOnClickOutside
                                onCancel={() =>
                                  this.setState({
                                    showSelectOwner: !showSelectOwner,
                                  })
                                }
                              >
                                {values.agency ? (
                                  <UserSelect
                                    agency={values.agency}
                                    selectUser={owner => {
                                      handleChange({
                                        target: {
                                          name: 'owner',
                                          value: owner,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotion',
                                          value: false,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotionAds',
                                          value: [],
                                        },
                                      });
                                      this.setState({
                                        showSelectOwner: !showSelectOwner,
                                      });
                                    }}
                                  />
                                ) : (
                                  <UserSelect
                                    listUserAgencies
                                    selectUser={owner => {
                                      handleChange({
                                        target: {
                                          name: 'owner',
                                          value: owner,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotion',
                                          value: false,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotionAds',
                                          value: [],
                                        },
                                      });
                                      this.setState({
                                        showSelectOwner: !showSelectOwner,
                                      });
                                    }}
                                  />
                                )}
                              </SweetAlert>
                              <div className={classes.section}>
                                <h6
                                  style={{
                                    color:
                                      touched.owner && errors.owner
                                        ? 'red'
                                        : 'black',
                                  }}
                                >
                                  {t('paidAd.form.fields.owner')}
                                </h6>
                                <div className={classes.row}>
                                  {values.owner && (
                                    <InfoUser user={values.owner} />
                                  )}
                                  <React.Fragment>
                                    <Button
                                      simple
                                      color="primary"
                                      id="selectOwner"
                                      onClick={() => {
                                        this.setState({
                                          showSelectOwner: true,
                                        });
                                      }}
                                    >
                                      {values.owner
                                        ? t('ad.form.fields.change')
                                        : t('ad.form.fields.select')}
                                    </Button>
                                    {values.owner && (
                                      <Button
                                        simple
                                        color="primary"
                                        onClick={() => {
                                          handleChange({
                                            target: {
                                              name: 'owner',
                                              value: null,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'promotion',
                                              value: false,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'promotionAds',
                                              value: [],
                                            },
                                          });
                                        }}
                                      >
                                        {t('paidAd.form.clean')}
                                      </Button>
                                    )}
                                  </React.Fragment>
                                  {touched.owner && errors.owner && (
                                    <p className={classes.errorCategoryField}>
                                      {t('errors.required')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          )}
                          <WithPermission
                            allowPermissions={['make_self_owner_autopaidad']}
                          >
                            <div style={{ marginTop: 15 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onClick={e => {
                                      handleChange(e);
                                      handleChange({
                                        target: {
                                          name: 'agency',
                                          value: null,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'owner',
                                          value: null,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotion',
                                          value: false,
                                        },
                                      });
                                      handleChange({
                                        target: {
                                          name: 'promotionAds',
                                          value: [],
                                        },
                                      });
                                    }}
                                    checked={values.fromOwner}
                                    name="fromOwner"
                                    checkedIcon={
                                      <Check className={classes.checkedIcon} />
                                    }
                                    icon={
                                      <Check
                                        className={classes.uncheckedIcon}
                                      />
                                    }
                                    classes={{
                                      checked: classes.checked,
                                    }}
                                  />
                                }
                                label={t('paidAd.form.fields.fromLoggedUser')}
                              />
                            </div>
                          </WithPermission>
                          <WithPermission
                            allowPermissions={[
                              'make_free_autopaidad',
                              'make_free_ownership_autopaidad',
                            ]}
                          >
                            <div style={{ marginTop: 15 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onClick={e => {
                                      handleChange(e);
                                    }}
                                    checked={values.free}
                                    name="free"
                                    checkedIcon={
                                      <Check className={classes.checkedIcon} />
                                    }
                                    icon={
                                      <Check
                                        className={classes.uncheckedIcon}
                                      />
                                    }
                                    classes={{
                                      checked: classes.checked,
                                    }}
                                  />
                                }
                                label={t('paidAd.form.fields.free')}
                              />
                            </div>
                          </WithPermission>
                          {/* show promotion input if there is promotions */}
                          {pickedSetup && pickedSetup.promotion && (
                            <div className={classes.section}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onClick={e => {
                                      handleChange(e);
                                    }}
                                    checked={values.promotion}
                                    name="promotion"
                                    checkedIcon={
                                      <Check className={classes.checkedIcon} />
                                    }
                                    icon={
                                      <Check
                                        className={classes.uncheckedIcon}
                                      />
                                    }
                                    classes={{
                                      checked: classes.checked,
                                    }}
                                  />
                                }
                                label={t('paidAd.form.fields.promotion')}
                              />
                              {values.promotion && (
                                <div
                                  style={{
                                    border:
                                      touched.promotion && errors.promotionAds
                                        ? '1px solid red'
                                        : '0',
                                  }}
                                >
                                  <AdsSelect
                                    selectedAds={values.promotionAds}
                                    customFilters={{
                                      owner: values.owner
                                        ? values.owner.id
                                        : user.id,
                                      schedule: pickedSetup.schedule
                                        ? pickedSetup.schedule
                                        : undefined,
                                      duration: pickedSetup.duration
                                        ? pickedSetup.duration
                                        : undefined,
                                      frequency: values.pack.frequency
                                        ? values.pack.frequency
                                        : undefined,
                                      startDateGte: values.date
                                        ? moment(values.date)
                                            .subtract(15, 'days')
                                            .startOf('day')
                                        : undefined,
                                      startDateLte: values.date
                                        ? moment(values.date)
                                            .add(15, 'days')
                                            .startOf('day')
                                        : undefined,
                                    }}
                                    adsUpdate={ads =>
                                      handleChange({
                                        target: {
                                          name: 'promotionAds',
                                          value: ads,
                                        },
                                      })
                                    }
                                    multiSelectItemRender={item => (
                                      <AdRow data={item} />
                                    )}
                                  />
                                </div>
                              )}
                              {touched.promotion && errors.promotionAds && (
                                <p className={classes.errorCategoryField}>
                                  {errors.promotionAds}
                                </p>
                              )}
                            </div>
                          )}
                          <div className={classes.section}>
                            <GridContainer direction="row-reverse">
                              <GridItem xs={12} sm={6}>
                                <div className={classes.contentBtns}>
                                  <Button
                                    id="save-ad"
                                    color="primary"
                                    type="submit"
                                  >
                                    {t('paidAd.form.button_create')}
                                  </Button>
                                </div>
                              </GridItem>
                              <GridItem xs={12} sm={6}>
                                <Button
                                  id="cancel-create"
                                  simple
                                  color="danger"
                                  onClick={this.handleCancelBtn}
                                >
                                  {t('common.cancel')}
                                </Button>
                              </GridItem>
                            </GridContainer>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </form>
                )}
              />
            </React.Fragment>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

/**
 * AdForm PropTypes
 * @param classes - styles received from withStyles
 * @param ad - Entity data
 * @param packs - Entity data
 * @param editMode - edition mode
 * @param errors - Errors data list
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with language information
 * @param exchangeRateQuery - Query for get an ExchangeRate
 * @callback onSave - callback called when user clicks save button
 */

PaidAdForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  packs: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  exchangeRates: PropTypes.instanceOf(Array).isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PaidAdFormWrapper = props => <PaidAdForm {...props} />;

export default withTranslation()(withStyles(adCreateStyles)(PaidAdFormWrapper));
